import {Injectable} from '@angular/core';
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {Category} from "../models/category.model";

/**
 * Serwis warstwy danych zarządzający strukturą kategorii w systemie.
 * Obsługuje pobieranie, aktualizację oraz bezpieczne usuwanie węzłów
 * przypisanych do konkretnych klientów lub modułów (np. FAQ).
 */
@Injectable({
  providedIn: 'root'
})
export class CategoryService {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Pobiera pełną strukturę kategorii przypisaną do konkretnego klienta (vClient).
   *
   * @param vClientUUID Unikalny identyfikator klienta (Sieci/Kliniki).
   * @returns Observable emitujący strukturę kategorii powiązaną z danym tenantem.
   */
  getCategories(vClientUUID: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/categories/${vClientUUID}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Weryfikuje zasady integralności bazy danych przed usunięciem kategorii.
   * Serwer sprawdza, czy kategoria nie posiada przypisanych rekordów potomnych,
   * aby zapobiec osieroceniu danych (Orphaned Records).
   *
   * @param vClientUUID Identyfikator klienta.
   * @param categoryUUID Identyfikator kategorii, którą użytkownik próbuje usunąć.
   * @returns Observable emitujący komunikat z serwera (np. zezwolenie lub błąd walidacji).
   */
  checkCategoryCanBeDeleted(vClientUUID: string, categoryUUID: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/cat/check-delete/${categoryUUID}/${vClientUUID}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.message;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wykonuje masową aktualizację (Update) drzewa kategorii dla danego klienta.
   * Frontend przesyła tu strukturę słownikową typu Map, która przed wysłaniem
   * jest konwertowana na standardowy obiekt JSON zrozumiały dla API.
   *
   * @param categories Mapa zaktualizowanych kategorii (Klucz -> Tablica kategorii).
   * @param uuid Identyfikator klienta, którego kategorie są aktualizowane.
   * @returns Observable emitujący zaktualizowaną strukturę z serwera.
   */
  updateCategories(categories, uuid: string) {
    const convMap = {};
    categories.forEach((value: Category[], key: string) => {
      convMap[key] = value;
    });
    console.log(JSON.stringify(convMap))
    return this.apiService.put<VetineoApiResponse>(`/dashboard/categories/${uuid}`, {'categories': convMap}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wykonuje masowy zapis (Create) nowych gałęzi/węzłów w drzewie kategorii.
   * Analogicznie jak w przypadku aktualizacji, transformuje strukturę Map na płaski obiekt.
   *
   * @param categories Mapa nowych kategorii do zapisania.
   * @param uuid Identyfikator klienta.
   * @returns Observable emitujący zapisaną strukturę kategorii.
   */
  addCategories(categories, uuid: string) {
    const convMap = {};
    categories.forEach((value: Category[], key: string) => {
      convMap[key] = value;
    });
    console.log(JSON.stringify(convMap))
    return this.apiService.post<VetineoApiResponse>(`/dashboard/categories/${uuid}`, {'categories': convMap}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera płaską listę systemowych (globalnych) kategorii dedykowanych
   * dla modułu Bazy Wiedzy / Najczęściej Zadawanych Pytań (FAQ).
   *
   * @returns Observable emitujący tablicę obiektów Category.
   */
  getSitesbiCategories() {
    return this.apiService.get<VetineoApiResponse>('/dashboard/categories/stsb/faq').pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedArrayCopyEver<Category, Category[]>(new Category(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }
}
